"use client";

import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { Drawer } from "./drawer";

export type DrawerOptions = {
  /** The panel heading, and the Radix dialog title (Figma 1052:63704). */
  title: ReactNode;
  /** Not drawn — mounted `sr-only` so the dialog is described for screen readers. */
  description?: string;
  side?: "right" | "left";
  /** Extra classes on the panel, for a width override such as `md:w-500`. */
  className?: string;
};

/** Return `false` to abort a dismiss. Registered by content via `useDrawerDismissGuard`. */
export type DrawerDismissGuard = () => boolean | Promise<boolean>;

export type DrawerState = {
  isOpen: boolean;
  content: ReactNode;
  options: DrawerOptions;
};

type DrawerContextValue = {
  isOpen: boolean;
  openDrawer: (content: ReactNode, options: DrawerOptions) => void;
  closeDrawer: () => void;
  /** Dismiss unconditionally — for use right after a successful submit. */
  forceCloseDrawer: () => void;
  setDismissGuard: (guard: DrawerDismissGuard | null) => void;
};

const CLOSED: DrawerState = { isOpen: false, content: null, options: { title: "" } };

const DrawerContext = createContext<DrawerContextValue | null>(null);

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) throw new Error("useDrawer must be used within a DrawerProvider.");
  return context;
};

export const DrawerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DrawerState>(CLOSED);
  const guardRef = useRef<DrawerDismissGuard | null>(null);

  const forceCloseDrawer = useCallback(() => {
    /* The provider outlives the content, so a stale guard would block the *next* drawer. */
    guardRef.current = null;
    setState((current) => ({ ...current, isOpen: false }));
  }, []);

  const closeDrawer = useCallback(async () => {
    const guard = guardRef.current;
    if (guard && !(await guard())) return;
    forceCloseDrawer();
  }, [forceCloseDrawer]);

  const openDrawer = useCallback((content: ReactNode, options: DrawerOptions) => {
    guardRef.current = null;
    setState({ isOpen: true, content, options: { side: "right", ...options } });
  }, []);

  const setDismissGuard = useCallback((guard: DrawerDismissGuard | null) => {
    guardRef.current = guard;
  }, []);

  /* Content is dropped only once the exit animation has finished (see drawer.tsx), so the panel
     doesn't blank out mid-slide. Every open replaces it anyway. */
  const dropContent = useCallback(() => setState(CLOSED), []);

  const value = useMemo<DrawerContextValue>(
    () => ({ isOpen: state.isOpen, openDrawer, closeDrawer, forceCloseDrawer, setDismissGuard }),
    [state.isOpen, openDrawer, closeDrawer, forceCloseDrawer, setDismissGuard],
  );

  return (
    <DrawerContext.Provider value={value}>
      {children}
      <Drawer state={state} onDismiss={closeDrawer} onExited={dropContent} />
    </DrawerContext.Provider>
  );
};

/**
 * Registers a dismiss guard for as long as the calling component is mounted — the canonical use is
 * an "unsaved changes" confirmation. The guard may be async, so it can be resolved by a user
 * decision rather than a synchronous check.
 */
export const useDrawerDismissGuard = (guard: DrawerDismissGuard) => {
  const { setDismissGuard } = useDrawer();
  const runGuard = useEffectEvent(() => guard());

  useEffect(() => {
    setDismissGuard(() => runGuard());
    return () => setDismissGuard(null);
  }, [setDismissGuard]);
};
